import { z } from "zod";

const optionalText = z.string().trim().max(255, "255자 이내로 입력해주세요.").nullable().optional();

export const productInformationSchema = z.object({
  sku: optionalText,
  product_moq: z.number().int("MOQ는 정수로 입력해주세요.")
    .min(1, "MOQ는 1 이상으로 입력해주세요.")
    .max(2147483647, "MOQ가 너무 큽니다.").nullable().optional(),
  hs_code: optionalText,
  origin: optionalText,
  origin_ja: optionalText,
  fabric_ko: optionalText,
  fabric_en: optionalText,
  fabric_ja: optionalText,
  wash_info_ko: optionalText,
  wash_info_en: optionalText,
  wash_info_ja: optionalText,
});

export type ProductInformation = z.infer<typeof productInformationSchema>;

export const productInformationTextFields = [
  ["sku", "SKU"],
  ["hs_code", "HS CODE"],
  ["origin", "원산지 (COO)"],
  ["origin_ja", "원산지 (일문)"],
  ["fabric_ko", "소재 / 혼용률 (국문)"],
  ["fabric_en", "소재 / 혼용률 (영문)"],
  ["fabric_ja", "소재 / 혼용률 (일문)"],
  ["wash_info_ko", "세탁 정보 (국문)"],
  ["wash_info_en", "세탁 정보 (영문)"],
  ["wash_info_ja", "세탁 정보 (일문)"],
] as const;

export const productInformationDefaults = (info: ProductInformation = {}) => ({
  sku: info.sku ?? "",
  product_moq: info.product_moq ?? null,
  hs_code: info.hs_code ?? "",
  origin: info.origin ?? "",
  origin_ja: info.origin_ja ?? "",
  fabric_ko: info.fabric_ko ?? "",
  fabric_en: info.fabric_en ?? "",
  fabric_ja: info.fabric_ja ?? "",
  wash_info_ko: info.wash_info_ko ?? "",
  wash_info_en: info.wash_info_en ?? "",
  wash_info_ja: info.wash_info_ja ?? "",
});

/** A cleared input must send null so edits clear the saved value as well. */
export const buildProductInformationPayload = (
  values: ProductInformation,
  editing?: {
    original: ProductInformation;
    changedFields?: Partial<Record<keyof ProductInformation, boolean>>;
  },
) => {
  const parsed = productInformationSchema.parse(values);
  const entries: [keyof ProductInformation, string | number | null][] = [
    ["product_moq", parsed.product_moq ?? null],
    ...productInformationTextFields.map(([field]): [typeof field, string | null] => [field, parsed[field] || null]),
  ];
  // Older or partial detail responses may omit saved fields. Send only fields we
  // actually received or the user edited, so unseen values cannot be cleared.
  return Object.fromEntries(entries.filter(([field]) => !editing ||
    Object.prototype.hasOwnProperty.call(editing.original, field) ||
    editing.changedFields?.[field]
  )) as ProductInformation;
};

export const productInformationDisplayValue = (value: string | number | null | undefined) =>
  value === null || value === undefined || String(value).trim() === "" ? "-" : String(value);
